import type { SrsSettings } from '@/types/admin'

export interface SrsSliderConfig {
  key: keyof SrsSettings
  label: string
  hint: string
  min: number
  max: number
  step: number
  suffix?: string
  format?: (value: number) => string
}

export interface SrsSliderGroup {
  title: string
  /** Why the lab cannot show this group at work, when it cannot. */
  note?: string
  sliders: SrsSliderConfig[]
}

const percent = (value: number) => `${Math.round(value * 100)}%`

/** The scheduling parameters, grouped by the stage of a card's life they shape. */
export const SRS_SLIDER_GROUPS: SrsSliderGroup[] = [
  {
    title: 'New cards',
    sliders: [
      {
        key: 'first_interval',
        label: 'First interval',
        hint: 'Days until a brand-new card comes back after its first pass.',
        min: 1,
        max: 7,
        step: 1,
        suffix: 'd',
      },
      {
        key: 'second_interval',
        label: 'Second interval',
        hint: 'The graduation step, before growth turns multiplicative.',
        min: 2,
        max: 30,
        step: 1,
        suffix: 'd',
      },
    ],
  },
  {
    title: 'Growth',
    sliders: [
      {
        key: 'interval_modifier',
        label: 'Interval modifier',
        hint: 'Brakes every computed interval. Below 1 slows the whole curve down.',
        min: 0.4,
        max: 1.5,
        step: 0.05,
        suffix: '×',
      },
      {
        key: 'hard_multiplier',
        label: 'Hard multiplier',
        hint: 'Scales the next interval when you rate Hard. Below 1 shortens it.',
        min: 0.5,
        max: 1.2,
        step: 0.05,
        suffix: '×',
      },
      {
        key: 'easy_multiplier',
        label: 'Easy multiplier',
        hint: 'Scales the next interval when you rate Easy.',
        min: 1,
        max: 2.5,
        step: 0.05,
        suffix: '×',
      },
      {
        key: 'ease_recovery',
        label: 'Ease recovery',
        hint: 'How much each Good lifts ease back towards 2.5. Zero is plain SM-2: only Easy raises it.',
        min: 0,
        max: 0.15,
        step: 0.01,
      },
    ],
  },
  {
    title: 'After a slip',
    sliders: [
      {
        key: 'lapse_recovery',
        label: 'Relearn step',
        hint: 'Days until a card you rated Again comes back. Zero keeps it in the current session.',
        min: 0,
        max: 21,
        step: 1,
        suffix: 'd',
      },
      {
        key: 'lapse_retention',
        label: 'Lapse retention',
        hint: 'Share of the pre-slip interval a word gets back once it passes the relearn step. Zero replays the new-card ladder.',
        min: 0,
        max: 1,
        step: 0.05,
        format: percent,
      },
      {
        key: 'lapse_ease_penalty',
        label: 'Ease penalty',
        hint: 'How much ease a slip costs. Lower ease means slower growth from then on.',
        min: 0,
        max: 0.5,
        step: 0.05,
      },
    ],
  },
  {
    title: 'Mastery',
    sliders: [
      {
        key: 'know_well_interval',
        label: 'Know well',
        hint: 'Where "I know this" parks a word. Must stay below the mastery bar.',
        min: 1,
        max: 120,
        step: 1,
        suffix: 'd',
      },
      {
        key: 'mastery_threshold',
        label: 'Mastery threshold',
        hint: 'The interval a card must reach to leave the review queue for good.',
        min: 21,
        max: 365,
        step: 1,
        suffix: 'd',
      },
    ],
  },
  {
    title: 'In practice',
    note: 'These act on real reviews and are not drawn in the lab, which assumes on-time reviews with no fuzz.',
    sliders: [
      {
        key: 'overdue_credit',
        label: 'Overdue credit',
        hint: 'Share of the days a review was late that count as time remembered.',
        min: 0,
        max: 1,
        step: 0.05,
        format: percent,
      },
      {
        key: 'fuzz',
        label: 'Fuzz',
        hint: 'Random spread on grown intervals, so words learned together stop falling due together.',
        min: 0,
        max: 0.25,
        step: 0.01,
        format: (value) => `±${percent(value)}`,
      },
    ],
  },
]
